import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";

/** `attachment` — something the turn produced or cited: an artifact path,
 * a fetched source, a written file.
 *
 * Every vendor vocabulary carries this (ACP resource links, OpenAI file
 * and citation annotations, Vercel's File and Source parts) and ours had
 * nowhere to put it, so artifacts lived only on the task page and a reader
 * of the transcript could not tell a turn had made anything.
 *
 * It is a chip, not a card: attachments cluster at the end of a turn, and
 * a card each would out-shout the turn they belong to. Opening is the
 * caller's business — without `onOpen` it renders as a plain label, which
 * is the right thing in a read-only transcript.
 *
 * ## What was wrong with the first chip
 *
 * It was outlined, and it sized itself to its text. Four of them stacked
 * came out as four hairline rectangles of four different widths — the
 * shape of disabled form controls, not of things a turn made. Three fixes,
 * all about the CLUSTER rather than the single chip:
 *
 * A filled surface instead of a stroke. A hairline is the loudest way to
 * bound a small element, and four strokes in a row read as four separate
 * controls; a soft fill reads as one group of related things. The stroke
 * comes back on hover, where it means "this one, and it is clickable".
 *
 * A tinted tile for the glyph. The kinds were previously a bare 11px
 * character in the text colour, which is both hard to tell apart and
 * optically ragged — every chip started at a different ink weight. A
 * constant-size tile gives the cluster one left edge and lets the kind
 * carry a colour, which is what makes four attachments scannable.
 *
 * A width cap. One long path made one 700px chip and threw the cluster's
 * rhythm away; capped, a row of them wraps into an even grid. The label
 * truncates from the LEFT (`direction: rtl`) because a path's tail
 * identifies it and its head repeats every sibling.
 *
 * The caller lays them out — a wrapped flex row is what a cluster wants —
 * and the chip only promises to be an even size inside one. */
@component({ name: "std-turn-attachment" })
export class TurnAttachment extends LonaWebComponent {
  private $kind: HTMLElement = this.$("kind");
  private $label: HTMLElement = this.$("label");
  private $meta: HTMLElement = this.$("meta");
  private $root: HTMLElement = this.$("root");
  private open: Option<() => void>;
  private bound: boolean = false;

  static makeWith(props: TurnAttachment.Props): TurnAttachment {
    return (TurnAttachment.make() as TurnAttachment).bind(props);
  }

  bind(props: TurnAttachment.Props): this {
    const kind = props.kind ?? "file";
    this.setAttribute("kind", kind);
    this.$kind.textContent = TurnAttachment.GLYPH[kind];
    this.$label.textContent = props.label;
    this.$meta.textContent = props.meta ?? "";
    this.$meta.style.display = props.meta ? "" : "none";
    this.open = props.onOpen ?? null;
    this.toggleAttribute("interactive", this.open != null);
    if (!this.bound) {
      this.bound = true;
      GESTURE_MANAGER.addPointerEvent(this.$root, {
        onClick: () => this.open?.(),
      });
    }
    return this;
  }

  static $styles = [
    css`
      :host {
        display: inline-block;
        max-width: 100%;
      }
      /* Filled, not stroked — see the note above. The 1px transparent
         border is held from the start so the hover stroke costs no
         reflow and the chip never grows under the pointer. */
      #root {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        max-width: var(--attachment-max-width, 264px);
        padding: 4px 9px 4px 4px;
        border: 1px solid transparent;
        border-radius: 6px;
        background: var(--bg-hover-very-light, rgba(127, 127, 127, 0.06));
        color: inherit;
        font: inherit;
        text-align: left;
        cursor: default;
      }
      :host([interactive]) #root {
        cursor: pointer;
      }
      :host([interactive]) #root:hover {
        border-color: var(--divider, rgba(127, 127, 127, 0.35));
        background: var(--bg-hover, rgba(127, 127, 127, 0.1));
      }
      /* One size for every kind: the tile is what gives a wrapped cluster a
         single left edge to run down. */
      #kind {
        flex: none;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        border-radius: 4px;
        --font-size: var(--turn-fine-size, var(--text-8px));
        --line-height-ratio: 1;
        /* Both derived from the kind's one hue below. --tile-ink is the
           --fg-* recipe written out, for the kinds whose twin token does
           not exist; a kind that has one takes it and this is unused. */
        --tile-ink: color-mix(
          in srgb,
          var(--tile-hue, currentColor) 72%,
          var(--text-pri, #1f2328) 28%
        );
        background: var(--tile-bg, color-mix(
          in srgb,
          var(--tile-hue, rgba(127, 127, 127, 0.8)) 16%,
          transparent
        ));
        color: var(--tile-fg, var(--text-sec, #6a7186));
      }
      /* The kind is a WASH before it is a glyph. Four glyphs at 12px are
         not four distinguishable things — the first revision's document
         and artifact marks were both "a small grey square" at reading
         distance. Tinting the tile itself is what makes a wrapped cluster
         sortable by eye; the glyph then only has to confirm it.

         Each kind declares ONE hue and both derivations come off it, and
         that is not tidiness — it is the fix for a bug this stack has now
         shipped three times. --fg-purple, --fg-teal and --fg-yellow do NOT
         exist in this app (only --fg-blue, --fg-red, --fg-green do), and an
         undefined custom property falls to var()'s LITERAL fallback, never
         to a related token. So var(--fg-purple, #8250df) shipped #8250df —
         a dark purple on a
         dark panel — and read in review as "some colors like purple is
         unreadable". --teal is undefined too, so the artifact tile fell
         all the way to #1a7f64 in BOTH the wash and the glyph.

         Writing the recipe instead of a hex is what removes the class: the
         --fg-* twins are each their hue mixed 72/28 toward --text-pri,
         so computing that lands where the token would have on either
         surface, and a hue that is itself missing degrades to one literal
         rather than to an unreadable pair. */
      :host([kind="image"]) #kind {
        --tile-hue: var(--purple, #8250df);
        --tile-fg: var(--fg-purple, var(--tile-ink));
      }
      :host([kind="link"]) #kind {
        --tile-hue: var(--blue, #0550ae);
        --tile-fg: var(--fg-blue, var(--tile-ink));
      }
      :host([kind="artifact"]) #kind {
        --tile-hue: var(--teal, #1a7f64);
        --tile-fg: var(--fg-teal, var(--tile-ink));
      }
      #label {
        min-width: 0;
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        /* The filename is this row's subject, so it takes the row
           register. The line box stays the tile's own 18px — that is the
           tile's height, a layout number rather than a type one. */
        --font-size: var(--small-size);
        color: var(--text-pri);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        /* A path's tail identifies it; its head repeats every sibling. */
        direction: rtl;
        text-align: left;
      }
      #meta {
        flex: none;
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
        --font-size: var(--turn-fine-size, var(--text-8px));
        color: var(--text-ter);
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <button id="root" type="button">
      <p class="small" id="kind"></p>
      <p class="small" id="label"></p>
      <p class="small" id="meta"></p>
    </button>
  `;
}

export namespace TurnAttachment {
  export type Kind = "file" | "image" | "link" | "artifact";

  /** Four shapes chosen to differ in SILHOUETTE, not in detail: ruled box,
   * split disc, arrow, diamond. Anything finer is a grey smudge at 12px. */
  export const GLYPH: Record<Kind, string> = {
    file: "▤",
    image: "◑",
    link: "↗",
    artifact: "◆",
  };

  export type Props = {
    label: string;
    kind?: Kind;
    /** Size, media type, line count — whatever identifies the thing. */
    meta?: string;
    onOpen?: () => void;
  };
}
